import { useEffect, useState } from 'react';
import { formatUsd, microsToUsd, tryUsdToMicros } from '@ithildin/shared';

import { api } from '../lib/api';
import { useSession } from '../hooks/useSession';

const CHANNELS = [
  { key: 'telegram', label: 'Telegram', blurb: 'A bot DMs you when a wallet writes.' },
  { key: 'discord', label: 'Discord', blurb: 'Get the same nudge as a Discord DM.' },
] as const;

export function Settings() {
  const { profile, refresh } = useSession();
  const [paused, setPaused] = useState(false);
  const [gate, setGate] = useState('');
  const [copied, setCopied] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [priceUsd, setPriceUsd] = useState('0');
  const [windowHours, setWindowHours] = useState(48);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.displayName ?? '');
    setBio(profile.bio ?? '');
    setPriceUsd(microsToUsd(profile.inboxPriceMicros));
    setWindowHours(profile.replyWindowHours);
    setPaused(profile.paused);
    setGate(profile.freeForCollection ?? '');
  }, [profile]);

  if (!profile) return <div className="page page-narrow" />;

  // Formatted defensively: this runs on every keystroke, including on inputs
  // like "1." that are valid to be typing but not yet a valid amount.
  const sharePath = `/w/${profile.address}`;
  const shareUrl = `${window.location.origin}${sharePath}`;

  const priceMicros = tryUsdToMicros(priceUsd || '0');
  const priceHint =
    priceMicros === null
      ? 'Enter an amount with at most 6 decimal places.'
      : BigInt(priceMicros) > 0n
        ? `Strangers escrow ${formatUsd(priceMicros)} to open a thread. You keep it only by replying in time.`
        : 'Zero means anyone can write to you for free.';

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const priceMicros = tryUsdToMicros(priceUsd || '0');
      if (priceMicros === null) {
        throw new Error('enter a price with at most 6 decimal places');
      }
      await api.updateProfile({
        displayName: displayName.trim() || null,
        bio: bio.trim() || null,
        inboxPriceMicros: priceMicros,
        replyWindowHours: windowHours,
        inboxPaused: paused,
        freeForCollection: gate.trim() || null,
      });
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not save');
    } finally {
      setSaving(false);
    }
  }

  async function link(channel: string) {
    try {
      const { url } = await api.linkChannel(channel);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'could not start linking');
    }
  }

  async function unlink(channel: string) {
    await api.unlinkChannel(channel);
    await refresh();
  }

  return (
    <div className="page page-narrow">
      <div className="page-head"><h2>Settings</h2></div>

      <section className="card" style={{ padding: 22, marginBottom: 20 }}>
        <h3 style={{ marginBottom: 16 }}>Your inbox</h3>

        <label className="label" htmlFor="name">Display name</label>
        <input
          id="name"
          className="field"
          value={displayName}
          maxLength={40}
          placeholder={profile.address.slice(0, 10)}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        <label className="label" style={{ marginTop: 16 }} htmlFor="bio">Bio</label>
        <input
          id="bio"
          className="field"
          value={bio}
          maxLength={280}
          placeholder="What you want to be contacted about"
          onChange={(e) => setBio(e.target.value)}
        />

        <label className="label" style={{ marginTop: 16 }} htmlFor="price">
          Price to message you
        </label>
        <div className="row" style={{ gap: 9 }}>
          <span className="meter-amount" style={{ fontSize: '1.1rem' }}>$</span>
          <input
            id="price"
            className="field field-narrow"
            value={priceUsd}
            inputMode="decimal"
            style={{ fontFamily: 'var(--data)', fontWeight: 700 }}
            onChange={(e) => setPriceUsd(e.target.value.replace(/[^\d.]/g, ''))}
          />
          <span className="hint">USDC</span>
        </div>
        <p className="hint" style={{ marginTop: 6 }}>
          {priceHint}
        </p>

        <label className="label" style={{ marginTop: 16 }} htmlFor="window">
          Minimum reply window you accept
        </label>
        <div className="row" style={{ gap: 9 }}>
          <input
            id="window"
            className="field field-narrow"
            type="number"
            min={1}
            max={720}
            value={windowHours}
            style={{ fontFamily: 'var(--data)', maxWidth: 110 }}
            onChange={(e) => setWindowHours(Number(e.target.value))}
          />
          <span className="hint">hours</span>
        </div>
        <p className="hint" style={{ marginTop: 6 }}>
          Senders cannot demand an answer faster than this.
        </p>

        <label className="label" style={{ marginTop: 18 }} htmlFor="gate">
          Free for holders of a collection
        </label>
        <input
          id="gate"
          className="field mono"
          value={gate}
          spellCheck={false}
          placeholder="Collection address — leave blank to charge everyone"
          onChange={(e) => setGate(e.target.value.trim())}
        />
        <p className="hint" style={{ marginTop: 6 }}>
          Anyone holding one of these skips the fee entirely.
        </p>

        <label
          className="row"
          style={{ marginTop: 18, gap: 10, cursor: 'pointer', alignItems: 'flex-start' }}
        >
          <input
            type="checkbox"
            checked={paused}
            style={{ marginTop: 4 }}
            onChange={(e) => setPaused(e.target.checked)}
          />
          <span>
            <span className="name">Pause paid messages</span>
            <span className="hint" style={{ display: 'block' }}>
              Keeps your price but turns away new paid threads, so you don't collect fees for
              messages you won't get to. Free messages still arrive.
            </span>
          </span>
        </label>

        <div className="row" style={{ marginTop: 20, gap: 12 }}>
          {saved && <span className="hint" style={{ color: 'var(--verdigris)' }}>Saved.</span>}
          <div className="spacer" />
          <button className="btn btn-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      <section className="card" style={{ padding: 22, marginBottom: 20 }}>
        <h3 style={{ marginBottom: 6 }}>Your link</h3>
        <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>
          Put this in a bio and anyone can reach you without knowing your address.
        </p>
        <div className="share">
          <code>{shareUrl}</code>
          <div className="spacer" />
          <button
            className="btn btn-sm"
            onClick={() => {
              void navigator.clipboard?.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
          <a className="btn btn-sm btn-quiet" href={sharePath}>View</a>
        </div>
      </section>

      <section className="card" style={{ padding: 22 }}>
        <h3 style={{ marginBottom: 6 }}>Where to reach you</h3>
        <p className="hint" style={{ marginTop: 0, marginBottom: 18 }}>
          Nobody who messages you can see any of this — not the account, not even whether
          you linked one. It is only used to tell you that something arrived.
        </p>

        <div className="stack" style={{ gap: 12 }}>
          {CHANNELS.map((c) => {
            const linked = profile.channels.find((ch) => ch.channel === c.key);
            return (
              <div
                key={c.key}
                className="row"
                style={{
                  gap: 14,
                  padding: '13px 15px',
                  border: '1px solid var(--line-soft)',
                  borderRadius: 'var(--r-md)',
                  flexWrap: 'wrap',
                }}
              >
                <div className="stack" style={{ gap: 2 }}>
                  <span className="name">{c.label}</span>
                  <span className="hint">{linked ? linked.displayHint : c.blurb}</span>
                </div>
                <div className="spacer" />
                {linked ? (
                  <button className="btn btn-quiet btn-sm" onClick={() => void unlink(c.key)}>
                    Unlink
                  </button>
                ) : (
                  <button className="btn btn-sm" onClick={() => void link(c.key)}>
                    Link
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {error && (
        <p className="hint hint-warn" style={{ marginTop: 14 }}>
          {error}
        </p>
      )}
    </div>
  );
}
